import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STATUS_META } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Plus, Search, FileEdit, CheckCircle2, Share2, Archive, Layers, ArrowRight, Loader2,
} from "lucide-react";

function StatCard({ icon: Icon, label, value, onClick, testid, tone }) {
  return (
    <button onClick={onClick} data-testid={testid} className="text-left">
      <Card className="p-5 hover:shadow-md transition-shadow border-[#C9A34A]/25 h-full">
        <div className={`grid h-10 w-10 place-items-center rounded-lg ${tone}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="mt-4 font-display text-4xl text-[#0B3B2E] leading-none">{value}</div>
        <div className="mt-1 text-sm text-[#14513D]/70">{label}</div>
      </Card>
    </button>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!search.trim()) {
      setResults(null);
      return;
    }
    const t = setTimeout(() => {
      api.get("/catalogues", { params: { search } }).then((r) => setResults(r.data)).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  if (!stats) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#0B3B2E]" /></div>;
  }

  const list = results ?? stats.recent;

  return (
    <div className="pgt-fade-up space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#C9A34A] font-semibold">Welcome, {user?.name}</div>
          <h1 className="font-display text-4xl sm:text-5xl text-[#0B3B2E]">Catalogue Dashboard</h1>
        </div>
        <Button onClick={() => navigate("/create")} data-testid="create-catalogue-btn" className="bg-[#0B3B2E] hover:bg-[#14513D] gap-2 h-11">
          <Plus className="h-4 w-4" /> Create New Catalogue
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard icon={Layers} label="Total Catalogues" value={stats.total} tone="bg-[#0B3B2E] text-[#C9A34A]" onClick={() => navigate("/drafts")} testid="stat-total" />
        <StatCard icon={FileEdit} label="Drafts" value={stats.draft} tone="bg-amber-100 text-amber-700" onClick={() => navigate("/drafts")} testid="stat-draft" />
        <StatCard icon={CheckCircle2} label="Ready" value={stats.ready} tone="bg-emerald-100 text-emerald-700" onClick={() => navigate("/ready")} testid="stat-ready" />
        <StatCard icon={Share2} label="Shared" value={stats.shared} tone="bg-sky-100 text-sky-700" onClick={() => navigate("/shared")} testid="stat-shared" />
        <StatCard icon={Archive} label="Archived" value={stats.archived} tone="bg-stone-200 text-stone-600" onClick={() => navigate("/archived")} testid="stat-archived" />
      </div>

      <div className="relative max-w-lg">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#14513D]/50" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by catalogue number, title or customer…"
          className="pl-10 h-11"
          data-testid="dashboard-search"
        />
      </div>

      <div>
        <h2 className="font-display text-2xl text-[#0B3B2E] mb-3">{results ? "Search Results" : "Recently Edited"}</h2>
        <div className="grid gap-3">
          {list.length === 0 && <div className="text-sm text-[#14513D]/60 py-8 text-center">No catalogues found.</div>}
          {list.map((c) => (
            <button
              key={c.id}
              onClick={() => navigate(`/catalogue/${c.id}`)}
              data-testid={`dashboard-cat-${c.catalogue_number}`}
              className="group flex items-center gap-4 rounded-xl border border-[#C9A34A]/25 bg-[#FAF7F0] px-4 py-3 text-left hover:shadow-md transition-all"
            >
              <div className="font-mono-id text-sm font-bold text-[#C9A34A] w-40 shrink-0">{c.catalogue_number}</div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-[#0B3B2E] truncate">{c.title || "Untitled"}</div>
                <div className="text-xs text-[#14513D]/60 truncate">
                  {c.customer_name ? `${c.customer_name} · ` : ""}{(c.products || []).length} products
                </div>
              </div>
              <Badge variant="outline" className={STATUS_META[c.status]?.cls}>{STATUS_META[c.status]?.label}</Badge>
              <ArrowRight className="h-4 w-4 text-[#C9A34A] opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          ))}
        </div>
      </div>

      {user?.role === "admin" && stats.by_executive?.length > 0 && (
        <div>
          <h2 className="font-display text-2xl text-[#0B3B2E] mb-3">Catalogues by Sales Executive</h2>
          <div className="flex flex-wrap gap-3">
            {stats.by_executive.map((e) => (
              <div key={e.name} className="rounded-lg border border-[#C9A34A]/25 bg-[#FAF7F0] px-4 py-2">
                <div className="text-sm font-semibold text-[#0B3B2E]">{e.name}</div>
                <div className="text-xs text-[#14513D]/60">{e.count} catalogues</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
